<template>
  <fts-design-modal
    class="fts-information-design-modal"
    ref="ftsDesignModal"
    :data-information-design-load="loading || autofillLoading ? 'loading' : 'loaded'"
  >
    <!-- title -->
    <template #title>
      <!-- system -->
      <template v-if="isSystem">
        <div class="design-modal-header-std__title">
          <i
            :class="informationIcon(formData.type)"
            class="design-modal-header-icon information-icon system"
          />
          <div class="design-modal-header-name">
            {{ formData.displayName }}
          </div>
        </div>
      </template>
      <!-- information -->
      <template v-else>
        <div class="design-modal-header-std__title with-hint">
          <i
            :class="informationIcon(formData.type)"
            class="design-modal-header-icon information-icon"
          ></i>
          <input
            type="text"
            class="fts-form-control design-modal-header-input fts-cursor-default"
            :value="formData.displayName"
            readonly
            v-if="isReadOnly"
          />
          <input
            type="text"
            placeholder="Please enter a name"
            class="fts-form-control design-modal-header-input"
            :class="{
              error: $v.formData.displayName && $v.formData.displayName.$error,
            }"
            v-model="formData.displayName"
            spellcheck="false"
            @focus="designNameHasFocus = true"
            @blur="designNameHasFocus = false"
            @input="dispatchAutofill"
            @keydown.enter="doneContinue"
            ref="designerName"
            v-else
          />
          <!-- parent -->
          <div class="information-parent" v-if="findParent">
            <div class="information-parent-seperator">in</div>
            <fts-label-group 
              :item="{
                icon: informationIcon(findParent.type),
                displayName: findParent.displayName
              }"
              information 
              class="information-parent-group" 
            />
          </div>
          <div class="design-modal-header-hint" v-if="headerHintHelper">
            press enter to continue
          </div>
        </div>
        <div class="design-modal-form-error" v-if="error('displayName')">
          {{ error("displayName") }}
        </div>
      </template>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Information</span>
      <span class="design-modal-header-description">
        specify the name and type of a field or input
      </span>
    </template>
    <!-- body -->
    <template #body>
      <!-- spinner -->
      <div class="design-modal-spinner" v-if="loading">
        <fts-spinner show />
      </div>
      <!-- system -->
      <template v-else-if="isSystem">
        <!-- description -->
        <div
          class="design-modal-form-group design-modal-body-description"
          v-if="formData.description"
        >
          {{ formData.description }}
        </div>
        <!-- document Id -->
        <template v-if="formData.id === 'documentId'">
          <!-- format -->
          <fts-form-element
            :formData="formData"
            :item="{
              fieldName: 'documentIdFormat',
              displayName: 'Format',
              type: 0,
            }"
            comfort-design
          >
            <fts-template-editor
              slot="formElement"
              slot-scope="{ formData, item }"
              :information="information"
              v-model="formData[item.fieldName]"
            />
          </fts-form-element>
          <!-- serial number -->
          <div class="serial-number" v-if="!serialNumberError">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">
                  Current Serial Number:
                  <span class="fts-text-primary fts-font-bold">{{serialNumber}}</span>
                </fts-form-label>
                <div class="fts-form-helper">
                  Set 0 to reset serial number. New serial number must be
                  greater or equal to the maximum serial number of all entries
                  in the module to prevent duplicate ID.
                </div>
              </div>
              <div class="design-modal-label-group-r">
                <fts-btn
                  outline
                  sm
                  text-transform-init
                  @click="
                    serialNumberExpand = !serialNumberExpand
                  "
                >
                  {{ serialNumberExpand ? "Cancel" : "Update" }}
                </fts-btn>
              </div>
            </div>
            <template v-if="serialNumberExpand">
              <fts-form-element
                :formData="serialNumberPayload"
                :item="{
                  fieldName: 'serialNumber',
                  displayName: 'New Serial Number',
                  type: 3,
                  decimalPlaces: 0,
                }"
                comfort-design />
              <div class="serial-number-action">
                <fts-btn
                  outline
                  sm
                  color="primary"
                  text-transform-init
                  @click="saveSerial">
                  Update Serial Number
                </fts-btn>
              </div>
            </template>
          </div>
        </template>
        <!-- format -->
        <fts-form-element
          :formData="formData"
          :item="{
            fieldName: 'title',
            displayName: 'Format',
            type: 0,
          }"
          comfort-design
          v-if="formData.id === 'title'"
        >
          <fts-template-editor
            slot="formElement"
            slot-scope="{ formData, item }"
            :information="information"
            v-model="formData[item.fieldName]"
          />
        </fts-form-element>
      </template>
      <!-- information -->
      <template v-else>
        <!-- ai annotation -->
        <fts-ai-annotation class="design-modal-ai-annotation" :hint="formData.ai_hint" />
        <!-- description -->
        <template v-if="isReadOnly">
          <div class="fts-form-group" v-if="formData.description">
            <fts-contenteditable
              :contenteditable="true"
              :value="formData.description"
              text-wrap
            />
          </div>
        </template>
        <div class="fts-form-group" v-else>
          <fts-contenteditable
            v-model="formData.description"
            text-wrap
            placeholder="enter description (optional)"
          />
        </div>
        <!-- type -->
        <fts-form-element
          :form-data="formData"
          :item="{
            displayName: 'Information Type',
            fieldName: 'type',
            icon: 'fts-icon-menu-caret-down',
            required: true,
            readOnly: isReadOnly || (isExist && !isInternal) || isComputed
          }"
          :validator="$v.formData"
          comfort-design
          ref="informationTypeInput"
        >
          <template #formElement="{ formData, item, onInput, onBlur }">
            <fts-select
              :form-data="formData"
              :item="item"
              label="type"
              :disabled="item.readOnly"
              comfort-design
              :options="getInformationTypeOptions"
              :selectable="(option) => !getExcludeTypes.some((x) => x == option.id)"
              append-dropdown-class="fts-information-design-modal-type__dropdown-menu"
              @input="onInput"
              @search:blur="onBlur"
              @option:selected="informationTypeSelected"
            >
              <div
                slot="selected-option-container"
                slot-scope="{ option }"
                class="information-type"
              >
                <i :class="option.icon" class="information-type-icon"></i>
                <div class="information-type-name">{{ option.type }}</div>
                <template v-if="option.isBeta || option.isAlpha">
                  &nbsp;<fts-release-badge :alpha="option.isAlpha" class="fts-align-top" />
                </template>
              </div>
              <template #option="{ option }">
                <div class="information-type">
                  <i :class="option.icon" class="information-type-icon"></i>
                  <div class="information-type-name">{{ option.type }}</div>
                  <template v-if="option.isBeta || option.isAlpha">
                    &nbsp;<fts-release-badge :alpha="option.isAlpha" class="fts-align-top" />
                  </template>
                  <div class="information-hint">
                    &nbsp;&nbsp;{{ getHint(option.id) }}
                  </div>
                </div>
                <div
                  class="information-hint-unsupported"
                  data-option="error" 
                  v-if="clientMode && getExcludeTypes.some((x) => x == option.id)"
                >
                  available in builder and studio
                </div>
              </template>
            </fts-select>

            <div class="information-type-hint-helper">
              {{ getHint(formData.type) }}
            </div>
          </template>
        </fts-form-element>
        <!-- connection type -->
        <template v-if="isConnectionType">
          <!-- connection -->
          <fts-form-element
            :form-data="formData"
            :item="{
              displayName: 'Module',
              fieldName: 'connectionId',
              icon: 'fts-icon-menu-caret-down',
              required: true,
              readOnly: isReadOnly,
            }"
            :validator="$v.formData"
            comfort-design
          >
            <fts-select
              slot="formElement"
              slot-scope="{ formData, item, onInput, onBlur }"
              :form-data="formData"
              :item="item"
              :options="moduleOptions"
              :disabled="item.readOnly"
              comfort-design
              @input="onInput"
              @search:focus="preloadModules"
              @search:blur="onBlur"
              @option:selected="updateConnection"
              @option:clear="updateConnection"
            >
              <fts-label-group
                slot="selected-option-container"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="workspaceModules"
                icon-class="design-modal-module-option-emoji"
                emoji
              />
              <fts-label-group
                slot="option"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="workspaceModules"
                icon-class="design-modal-module-option-emoji"
                emoji
              />
            </fts-select>
          </fts-form-element>
          <!-- connection field -->
          <fts-form-element
            :form-data="formData"
            :item="{
              displayName: 'Display Information',
              fieldName: 'connectionField',
              icon: 'fts-icon-menu-caret-down',
              required: true,
              readOnly: isReadOnly,
            }"
            :validator="$v.formData"
            comfort-design
            v-if="formData.connectionId"
          >
            <fts-select
              slot="formElement"
              slot-scope="{ formData, item, onInput, onBlur }"
              :form-data="formData"
              :item="item"
              reduceKey="displayName"
              label="displayName"
              :options="displayInformationSelection"
              :key="formData.connectionField"
              :disabled="item.readOnly"
              comfort-design
              @input="onInput"
              @search:blur="onBlur"
              @option:selected="updateConnectionField"
              @option:clear="updateConnectionField"
            >
              <div
                slot="selected-option-container"
                slot-scope="{ option }"
                class="information-type"
                :class="{ system: option.system }"
              >
                <i
                  :class="informationIcon(option.type)"
                  class="information-type-icon"
                ></i>
                <div class="information-type-name">
                  {{ option.displayName }}
                </div>
              </div>
              <div
                slot="option"
                slot-scope="{ option }"
                class="information-type"
                :class="{ system: option.system }"
              >
                <i
                  :class="informationIcon(option.type)"
                  class="information-type-icon"
                ></i>
                <div class="information-type-name">
                  {{ option.displayName }}
                </div>
              </div>
            </fts-select>
          </fts-form-element>
        </template>
        <!-- select:switch -->
        <template v-if="formData.type == 27 || formData.type == 28">
          <div class="information-select-switch">
            <div
              class="select-switch"
              :class="{'select-switch--active': !formData.reference}"
              @click="toggleSelectSwitch(false)"
              v-if="!isReadOnly || (isReadOnly && !formData.reference)"
            >
              User-Defined
            </div>
            <div
              class="select-switch"
              :class="{'select-switch--active': formData.reference}"
              @click="toggleSelectSwitch(true)"
              v-if="(
                !isReadOnly || (isReadOnly && formData.reference)
              ) && (formData.type == 27 || (isInternal && formData.type == 28))"
            >
              Reference<template v-if="formData.type == 28">&nbsp;<fts-release-badge alpha /></template>
            </div>
          </div>
        </template>
        <!-- select:reference -->
        <div
          class="design-modal-form-group design-modal-card design-modal-card-mt design-modal-card-pb"
          v-if="(formData.type === 27 || formData.type === 28) && formData.reference"
        >
          <div class="fts-row gutters">
            <!-- reference:module -->
            <fts-form-element
              :form-data="formData"
              :item="{
                displayName: 'Reference Module',
                fieldName: 'referenceModuleFieldId',
                icon: 'fts-icon-menu-caret-down',
                required: true,
                readOnly: isReadOnly
              }"
              :validator="$v.formData"
              comfort-design
              class="fts-col"
              key="referenceModuleFieldId"
            >
              <template #formElement="{formData, item, onInput, onBlur}">
                <fts-select
                :form-data="formData"
                :item="item"
                label="displayName"
                :options="referenceInformation"
                :disabled="item.readOnly"
                comfort-design
                @input="onInput"
                @search:blur="onBlur"
                @option:selected="updateReferenceModule"
                @option:clear="updateReferenceModule"
              >
                <div
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  class="information-type"
                >
                  <i :class="informationIcon(option.type)" class="information-type-icon"></i>
                  <div class="information-type-name">{{ option.displayName }}</div>
                  <!-- module -->
                  <fts-label-group
                    :item="getModule(option.connectionId)"
                    emoji
                    :emoji-size="12"
                    class="information-module-tag"
                    v-if="option.connectionId && getModule(option.connectionId)"
                  />
                  <!-- parent -->
                  <div class="information-parent-tag" v-if="option.parentDisplayName">
                    <div class="information-parent-tag-seperator">in</div>
                    <fts-label-group
                      :item="{
                        icon: informationIcon(option.parentType),
                        displayName: option.parentDisplayName
                      }"
                      information  
                    />
                  </div>
                </div>
                <div slot="option" slot-scope="{ option }" class="information-type">
                  <i :class="informationIcon(option.type)" class="information-type-icon"></i>
                  <div class="information-type-name">{{ option.displayName }}</div>
                  <!-- module -->
                  <fts-label-group
                    :item="getModule(option.connectionId)"
                    emoji
                    :emoji-size="12"
                    class="information-module-tag"
                    v-if="option.connectionId && getModule(option.connectionId)"
                  />
                  <!-- parent -->
                  <div class="information-parent-tag" v-if="option.parentDisplayName">
                    <div class="information-parent-tag-seperator">in</div>
                    <fts-label-group 
                      :item="{
                        icon: informationIcon(option.parentType),
                        displayName: option.parentDisplayName
                      }"
                      information 
                    />
                  </div>
                </div>
                </fts-select>

                <div
                  style="margin-top: 0.275rem;font-size: 0.75rem;color: var(--fts-default);"
                  v-if="formData.referenceModule && !formData.referenceModuleId"
                >
                  Configuration incomplete, please select Reference Module. Hint: 
                  <span class="fts-underline">{{formData.referenceModule}}</span>
                </div>
              </template>
            </fts-form-element>
            <!-- reference:module table -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'referenceModuleTable',
                icon: 'fts-icon-menu-caret-down',
                required: true,
                readOnly: isReadOnly,
              }"
              :validator="$v.formData"
              comfort-design
              class="fts-col"
              key="referenceModuleTable"
              v-if="formData.referenceModule"
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                :form-data="formData"
                :item="item"
                reduce-key="displayName"
                label="displayName"
                :options="referenceModuleTables"
                :disabled="item.readOnly"
                comfort-design
                @input="onInput"
                @search:focus="preloadReferenceModuleTable"
                @search:blur="onBlur"
                @option:selected="updateReferenceModuleTable"
                @option:clear="updateReferenceModuleTable"
              >
                <div
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  class="information-type"
                >
                  <i :class="informationIcon(option.type)" class="information-type-icon"></i>
                  <div class="information-type-name">{{ option.displayName }}</div>
                </div>
                <div slot="option" slot-scope="{ option }" class="information-type">
                  <i :class="informationIcon(option.type)" class="information-type-icon"></i>
                  <div class="information-type-name">{{ option.displayName }}</div>
                </div>
              </fts-select>
            </fts-form-element>
            <!-- reference:display field -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'referenceDisplayField',
                icon: 'fts-icon-menu-caret-down',
                required: true,
                readOnly: isReadOnly,
              }"
              :validator="$v.formData"
              comfort-design
              class="fts-col"
              key="referenceDisplayField"
              v-if="formData.referenceModuleTable"
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                :form-data="formData"
                :item="item"
                reduce-key="displayName"
                label="displayName"
                :options="referenceDisplayFields"
                :disabled="item.readOnly"
                comfort-design
                @input="onInput"
                @search:blur="onBlur"
                @option:selected="updateReferenceDisplayField"
                @option:clear="updateReferenceDisplayField"
              >
                <div
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  class="information-type"
                >
                  <i :class="informationIcon(option.type)" class="information-type-icon"></i>
                  <div class="information-type-name">{{ option.displayName }}</div>
                </div>
                <div slot="option" slot-scope="{ option }" class="information-type">
                  <i :class="informationIcon(option.type)" class="information-type-icon"></i>
                  <div class="information-type-name">{{ option.displayName }}</div>
                </div>
              </fts-select>
            </fts-form-element>
          </div>
        </div>
        <!-- select:options -->
        <div
          class="design-modal-form-group design-modal-card design-modal-card-mt design-modal-card-pb"
          v-if="(formData.type === 27 || formData.type === 28) && !formData.reference"
        >
          <fts-form-element
            :form-data="formData"
            :item="{
              displayName: 'Options',
              fieldName: 'optionList',
              icon: 'fts-icon-menu-caret-down',
            }"
            comfort-design
            key="optionList"
          >
            <draggable
              slot="formElement"
              slot-scope="{ formData, item }"
              handle=".option-handle"
              draggable=".option-draggable"
              v-model="formData[item.fieldName]"
              v-bind="dragOptions"
              class="information-options"
              @start="draggableOnDrag('informationOptions', true)"
              @end="draggableOnDrag('informationOptions', false)"
              ref="informationOptions"
            >
              <div
                class="option-draggable"
                v-for="(option, optionIndex) in formData.optionList"
                :key="option.id"
              >
                <div class="option-wrapper">
                  <font-awesome-icon
                    :icon="['fas', 'grip-vertical']"
                    class="option-handle"
                    v-if="!isReadOnly"
                  />
                  <div class="option-l">
                    <div
                      class="option-name"
                      :style="{ 'background-color': getOptionColor(option) }"
                      v-if="isReadOnly"
                    >
                      {{option.name}}
                    </div>
                    <fts-contenteditable
                      text-wrap
                      v-model="option.name"
                      :style="{ 'background-color': getOptionColor(option) }"
                      placeholder="Please enter a name"
                      class="option-name"
                      @input="fieldValidatorTouch('optionList', option.id, 'name')"
                      @keydown:enter="addSelectOption"
                      :ref="`option${optionIndex}`"
                      v-else
                    />
                  </div>
                  <div class="option-action" v-if="!isReadOnly">
                    <swatches
                      class="option-color"
                      v-model="option.color"
                      row-length="5"
                      :swatches="colorSchemes"
                      shapes="circles"
                      popover-x="left"
                      popover-y="top"
                      :trigger-style="!option.color ? {'background-color': getOptionColor(option), 'border': 'none'} : null"
                      :swatch-size="25"
                    />
                    <fts-btn
                      icon
                      text
                      @click="removeSelectOption(option)"
                    >
                      <font-awesome-icon :icon="['far', 'trash-alt']" />
                    </fts-btn>
                  </div>
                </div>
                <div
                  class="option-error"
                  v-if="fieldValidatorInvalid('optionList', option.id, 'name')"
                >
                  <template v-if="!(fieldValidator('optionList', option.id, 'name') && fieldValidator('optionList', option.id, 'name').unique)">
                    Option already exists.
                  </template>
                  <template v-else>
                    Option Name is required.
                  </template>
                </div>
              </div>
              <!-- footer -->
              <fts-btn
                slot="footer"
                sm
                outline
                color="primary"
                text-transform-init
                class="option-btn-add"
                @click="addSelectOption"
                v-if="!isReadOnly"
              >
                <font-awesome-icon :icon="['fas', 'plus']" /> Add Option
              </fts-btn>
            </draggable>
          </fts-form-element>
          <!-- radio -->
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'radio',
              input: 'toggleSwitch',
              readOnly: isReadOnly,
              helper: 'Switching the view from dropdown selections to radio buttons. Currently only applicable to the web (workstation).'
            }"
            comfort-design
            key="optionRadio"
            v-if="!Boolean(findParent) && formData.type === 27"
          />
        </div>
        <!-- fields -->
        <fts-form-element
          :form-data="formData"
          :item="{
            displayName: 'Fields',
            fieldName: 'fields',
            icon: 'fts-icon-list-table',
            required: true,
            readOnly: isReadOnly,
          }"
          :validator="$v.formData"
          comfort-design
          key="fields"
          v-if="formData.type === 14 || formData.type === 15"
        >
          <draggable
            slot="formElement"
            slot-scope="{ formData, item }"
            handle=".field-handle"
            draggable=".field-wrapper"
            v-model="formData[item.fieldName]"
            v-bind="dragOptions"
            class="information-fields"
            @start="draggableOnDrag('informationFields', true)"
            @end="draggableOnDrag('informationFields', false)"
            ref="informationFields"
          >
            <div
              class="field-wrapper draggable"
              v-for="x in formData.fields"
              :key="x.id"
              @click="openInformationField(x)"
            >
              <font-awesome-icon
                :icon="['fas', 'grip-vertical']"
                class="field-handle stopPropagation"
                v-if="!isReadOnly"
              />

              <div class="information-type">
                <i
                  :class="informationIcon(x.type)"
                  class="information-type-icon"
                ></i>
                <div class="information-type-name">{{ x.displayName }}</div>
              </div>

              <div class="field-action" v-if="!isReadOnly && !(params && params.disabledDelete)">
                <!-- per-column access lock (information-level authorization on the sub-field id),
                     e.g. hide an invoice line-item's Unit Price column from some users -->
                <fts-auth
                  type="information"
                  :unitId="x.id"
                  :title="x.displayName"
                  class="stopPropagation"
                  v-if="x.id && !x.system"
                />
                <fts-btn
                  icon
                  text
                  class="stopPropagation"
                  @click="removeInformationField(x)"
                >
                  <font-awesome-icon :icon="['far', 'trash-alt']" />
                </fts-btn>
              </div>
            </div>
            <!-- footer -->
            <fts-btn
              slot="footer"
              sm
              outline
              color="primary"
              text-transform-init
              class="field-btn-add"
              @click="openInformationField"
              v-if="!isReadOnly"
            >
              <font-awesome-icon :icon="['fas', 'plus']" /> Add Field
            </fts-btn>
          </draggable>
        </fts-form-element>
        <!-- location:autocomplete -->
        <div v-if="isInternal && formData.type === 9">
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'autocomplete',
              input: 'toggleSwitch',
              readOnly: isReadOnly,
              helper: 'Enables Google Maps place autocomplete.'
            }"
            comfort-design
          >
            <template #formLabel="{ label }">
              {{label}} <fts-release-badge alpha class="fts-align-top" />
            </template>
          </fts-form-element>
        </div>
        <!-- formula mode -->
        <fts-collapsible-element
          :condition="formulaMode"
          v-if="isInternal"
        >
          <template #toggle>
            <fts-form-label icon="fts-icon-check-square" placement="top">
              Formula<fts-release-badge alpha class="fts-align-top" />
            </fts-form-label>
            <fts-toggle-switch
              v-model="formulaMode"
              :size="18"
              mat-class
            />
          </template>
          <div slot="content">
            <fts-formula
              :variables="formulaVariables"
              :module-information="formulaModuleInformation"
              v-model="formData.formula"
              computed-field
              :column-context="isColumnContext"
              class="design-modal-advanced-content"
            />
            <div class="design-modal-form-hint" v-if="formulaValidating">
              Validating formula…
            </div>
            <div
              class="design-modal-form-error"
              v-else-if="formulaError"
            >
              {{ formulaError }}
            </div>
          </div>
        </fts-collapsible-element>
        <!-- advanced -->
        <fts-collapsible-element v-model="advanced">
          <fts-btn
            slot="toggle"
            slot-scope="{ isVisible, toggle }"
            text
            text-transform-init
            :class="{ open: isVisible }"
            class="design-modal-advanced-toggle"
            @click="toggle()"
          >
            <div
              class="design-modal-advanced-toggle-error"
              v-if="advancedSettingsError"
            />
            Advanced settings
            <font-awesome-icon
              :icon="['fas', 'caret-right']"
              class="design-modal-advanced-toggle-icon"
            />
          </fts-btn>
          <div slot="content" class="design-modal-advanced-content comfort-form-design fts-row gutters">
            <!-- field name -->
            <div class="fts-col" v-if="isInternal">
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'fieldName',
                  type: 0,
                  required: true,
                  readOnly: isReadOnly,
                }"
                :validator="$v.formData"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge alpha class="fts-align-top" />
                </template>
                <template #errorMessage>
                  {{error("fieldName")}}
                </template>
              </fts-form-element>
            </div>
            <!-- locus display template -->
            <div
              class="fts-col design-modal-form-group"
              data-label="Display Template"
              key="displayTemplate"
              v-if="
                formData.type === 7 || 
                (formData.type === 20 && isBeta)
              "
            >
              <fts-form-label icon="fts-icon-formula" placement="top">
                Display Template <fts-release-badge beta class="fts-align-top" v-if="formData.type === 20" />
              </fts-form-label>
              <fts-template-editor
                type="text"
                :information="displayTemplateOptions"
                v-model="formData.displayTemplate"
                :disabled="isReadOnly"
                @input="updateDisplayTemplate"
              />
            </div>
            <!-- locus value template -->
            <div
              class="fts-col design-modal-form-group"
              data-label="Value Template"
              key="valueTemplate"
              v-if="isBeta && formData.type === 7 && formData.displayTemplate"
            >
              <fts-form-label icon="fts-icon-formula" placement="top">
                Value Template <fts-release-badge class="fts-align-top" />
              </fts-form-label>
              <div class="fts-form-helper">
                If you wish the value different from the display template, then
                you may custom your own value template. And it is optional.
              </div>
              <fts-template-editor
                type="text"
                :information="displayTemplateOptions"
                v-model="formData.valueTemplate"
                :disabled="isReadOnly"
              />
            </div>
            <!-- map from -->
            <div class="fts-col" v-if="isInternal">
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'from',
                  type: 0,
                  readOnly: isReadOnly,
                  helper: 'For connectors.'
                }"
                :validator="$v.formData"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge alpha class="fts-align-top" />
                </template>
              </fts-form-element>
            </div>
            <!-- unique key -->
            <div
              class="fts-col"
              key="uniqueKey"
              v-if="formData.type === 0 && !Boolean(findParent)"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'uniqueKey',
                  input: 'toggleSwitch',
                  readOnly: isReadOnly,
                  helper: 'Enables unique identification to prevent duplication.'
                }"
                comfort-design
              />
            </div>
            <!-- can add -->
            <div
              class="fts-col"
              key="canAddNew"
              v-if="
                isBeta &&
                (formData.type === 7 || formData.type === 20)
              "
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'canAddNew',
                  input: 'toggleSwitch',
                  readOnly: isReadOnly,
                  helper: 'Enables shortcut for adding new options.'
                }"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge class="fts-align-top" />
                </template>
              </fts-form-element>
            </div>
            <!-- allow other -->
            <div
              class="fts-col"
              key="allowOther"
              v-if="isBeta && formData.type === 7"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'allowOther',
                  input: 'toggleSwitch',
                  readOnly: isReadOnly,
                  helper: 'To allow other value not in the options.'
                }"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge class="fts-align-top" />
                </template>
              </fts-form-element>
            </div>
            <!-- wildcard -->
            <div
              class="fts-col"
              key="wildcard"
              v-if="isBeta && formData.type === 7"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'wildcard',
                  input: 'toggleSwitch',
                  readOnly: isReadOnly,
                  helper: 'To allow a value from another module, working with automation blocks or scripting is necessary to set it. Currently only applicable to the web (workstation).'
                }"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge class="fts-align-top" />
                </template>
              </fts-form-element>
            </div>
            <!-- assign -->
            <div
              class="fts-col"
              key="assign"
              v-if="isBeta && formData.type === 20"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'assign',
                  input: 'toggleSwitch',
                  readOnly: isReadOnly,
                  helper: 'To set value as the assignee.'
                }"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge class="fts-align-top" />
                </template>
              </fts-form-element>
            </div>
            <!-- dashboard due -->
            <div
              class="fts-col"
              key="dashboardDue"
              v-if="isBeta && (formData.type === 5 || formData.type === 6)"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'dashboardDue',
                  input: 'toggleSwitch',
                  readOnly: isReadOnly,
                  helper: 'To set value as the due date for task dues in dashboard.'
                }"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge class="fts-align-top" />
                </template>
              </fts-form-element>
            </div>
            <!-- date time format -->
            <div
              class="fts-col"
              key="predefinedFormatId"
              v-if="formData.type === 5 || formData.type === 6"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'predefinedFormatId',
                  displayName: 'Display Format',
                  icon: 'fts-icon-menu-caret-down',
                  readOnly: isReadOnly,
                }"
                comfort-design
              >
                <fts-select
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  :formData="formData"
                  :item="item"
                  :options="dateFormats"
                  :disabled="item.readOnly"
                  comfort-design
                />
              </fts-form-element>
            </div>
            <!-- custom format -->
            <div
              class="fts-col"
              key="customFormat"
              v-if="isBeta && formData.type === 3"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'customFormat',
                  type: 0,
                  readOnly: isReadOnly,
                }"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge class="fts-align-top" />
                </template>
              </fts-form-element>
            </div>
            <!-- decimal places -->
            <div
              class="fts-col"
              key="decimalPlaces"
              v-if="formData.type === 3"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'decimalPlaces',
                  type: 2,
                  min: 0,
                  readOnly: isReadOnly,
                }"
                comfort-design
              />
            </div>
            <!-- max file size -->
            <div
              class="fts-col"
              key="maxFileSize"
              v-if="isBeta && [10, 11, 12, 13].indexOf(formData.type) > -1"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'maxFileSize',
                  displayName: 'Max File Size (MB)',
                  type: 2,
                  min: 0,
                  readOnly: isReadOnly,
                }"
                comfort-design 
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge class="fts-align-top" />
                </template>
              </fts-form-element>
            </div>
            <!-- camera only -->
            <div
              class="fts-col"
              key="cameraOnly"
              v-if="[10, 11].indexOf(formData.type) > -1"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'cameraOnly',
                  input: 'toggleSwitch',
                  helper: 'Only applicable to the App.'
                }"
                comfort-design 
              />
            </div>
            <!-- column width (table field) -->
            <div
              class="fts-col"
              key="columnWidth"
              v-if="isTableField"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'columnWidth',
                  type: 2,
                  min: 0,
                  readOnly: isReadOnly,
                }"
                comfort-design
              />
            </div>
            <!-- simple (table field) -->
            <div
              class="fts-col"
              key="simple"
              v-if="formData.type === 14"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'simple',
                  input: 'toggleSwitch',
                  readOnly: isReadOnly,
                }"
                comfort-design
              />
            </div>
            <!-- transaction (number, currency and integer) -->
            <div
              class="fts-col"
              key="transaction"
              v-if="isInternal && [2,3,4].indexOf(formData.type) > -1"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'transaction',
                  input: 'text',
                  readOnly: isReadOnly,
                }"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge alpha class="fts-align-top" />
                </template>
              </fts-form-element>
            </div>
          </div>
        </fts-collapsible-element>
        <!-- advanced:error -->
        <div
          class="design-modal-form-error"
          v-if="!advanced && advancedSettingsError"
        >
          Error found in Advanced settings.
        </div>
        <!-- information design modal -->
        <fts-information-design-modal
          child
          :parent="formData"
          :parent-fields="formData && formData.fields"
          :disable-continue="disableContinue"
          :is-capable-add-information="isCapableAddInformation"
          :is-capable-change-information="isCapableChangeInformation"
          ref="ftsInformationDesignModal"
        />
        <!-- information delete confirmation -->
        <fts-information-delete-confirmation ref="ftsInformationDeleteConfirmation" />
        <!-- D11: referrer checklist for sub-fields/options this save drops -->
        <fts-canvas-guard-dialog ref="ftsCanvasGuardDialog" />
      </template>
    </template>
    <!-- footer:left -->
    <template #footerLeft>
      <!-- remove -->
      <fts-btn
        text
        icon
        @click="remove" 
        v-if="!isReadOnly && !loading && !isSystem && isExist && !(params && params.disabledDelete)"
      >
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <!-- close -->
      <fts-btn
        text
        @click="reset"
        v-if="isReadOnly"
      >
        Close
      </fts-btn>
      <template v-else>
        <!-- cancel -->
        <fts-btn
          text
          :disabled="submitting"
          @click="beforeClose"
        >
          Cancel
        </fts-btn>

        <fts-btn
          color="primary"
          loading
          v-if="submitting"
        >
          Submitting
        </fts-btn>
        <template v-else>
          <!-- done -->
          <fts-btn
            color="primary"
            outline
            :disabled="loading"
            @click="done"
          >
            <font-awesome-icon :icon="['fas', 'check']" /> Ok
          </fts-btn>
          <!-- done and continue -->
          <fts-btn
            color="primary"
            outline
            @click="doneContinue"
            v-if="continuable"
          >
            <font-awesome-icon :icon="['fas', 'chevron-right']" /> Continue
          </fts-btn>
        </template>
      </template>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import { required } from "vuelidate/lib/validators";

import changesets from "diff-json";
import draggable from "vuedraggable";
import Swatches from "vue-swatches";

import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore } from "../store/canvasDesignStore";
import * as canvasOps from "../../business/canvasOps";
import FTSCanvasGuardDialog from "./FTSCanvasGuardDialog";
import commons from "../commons/commons";
import validator from "../commons/designValidator";
import { lightenPalette } from "../commons/colors";
import { Information } from '../commons/flags';

import FTSDesignModal from "./FTSDesignModal";
import FTSFormula from "./FTSFormula";
import FTSInformationDeleteConfirmation from './FTSInformationDeleteConfirmation';
import FTSBtn from "../elements/FTSBtn";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSFormElement from "../elements/FTSFormElement";
import FTSFormLabel from "../elements/FTSFormLabel";
import FTSSelect from "../elements/FTSSelect";
import FTSTemplateEditor from "./FTSTemplateEditor";
import FTSLabelGroup from "../elements/FTSLabelGroup";
import FTSSpinner from "../elements/FTSSpinner";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";
import FTSAiAnnotation from "./FTSAiAnnotation";
import FTSAuth from "./FTSAuth";

/* workaround */
const informationReservedKeywords =
  validator.informationReservedKeywords.concat(["username"]);
/* end workaround */

export default {
  name: "fts-information-design-modal",
  components: {
    draggable,
    Swatches,
    "fts-design-modal": FTSDesignModal,
    "fts-formula": FTSFormula,
    "fts-information-delete-confirmation": FTSInformationDeleteConfirmation,
    "fts-canvas-guard-dialog": FTSCanvasGuardDialog,
    "fts-btn": FTSBtn,
    "fts-contenteditable": FTSContenteditable,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-form-element": FTSFormElement,
    "fts-form-label": FTSFormLabel,
    "fts-select": FTSSelect,
    "fts-template-editor": FTSTemplateEditor,
    "fts-label-group": FTSLabelGroup,
    "fts-spinner": FTSSpinner,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-ai-annotation": FTSAiAnnotation,
    "fts-auth": FTSAuth,
  },
  props: {
    child: Boolean,
    parent: Object,
    parentFields: Array,
    disableContinue: Boolean,
    isCapableAddInformation: {
      type: Boolean,
      default: true
    },
    isCapableChangeInformation: {
      type: Boolean,
      default: true
    },
    clientMode: Boolean
  },
  data: () => ({
    serialNumberError: false,
    serialNumberExpand: false,
    serialNumberPayload: {},
    serialNumber: 0,
    formData: null,
    originalData: null,
    resolve: null,
    continueCallBack: null,
    params: null,
    advanced: false,
    moduleOptions: [],
    loading: false,
    submitting: false,
    autofillLoading: false,
    designNameHasFocus: false,
    /* formula validation */
    formulaValidating: false,
    formulaError: null,
    /* preference */
    referenceModuleTables: [],
    /* drag options */
    dragOptions: commons.dragOptions,
    /* date formats */
    dateFormats: commons.dateFormats,
    /* color schemes */
    colorSchemes: lightenPalette,
    /* hints */
    hints: [
      { id: Information.Text.ID, hint: Information.Text.Hint },
      { id: Information.YesNo.ID, hint: Information.YesNo.Hint },
      { id: Information.Integer.ID, hint: Information.Integer.Hint },
      { id: Information.Number.ID, hint: Information.Number.Hint },
      { id: Information.Money.ID, hint: Information.Money.Hint },
      { id: Information.Date.ID, hint: Information.Date.Hint },
      { id: Information.DateTime.ID, hint: Information.DateTime.Hint },
      { id: Information.Module.ID, hint: Information.Module.Hint },
      { id: Information.Modules.ID, hint: Information.Modules.Hint },
      { id: Information.Location.ID, hint: Information.Location.Hint },
      { id: Information.Image.ID, hint: Information.Image.Hint },
      { id: Information.Images.ID, hint: Information.Images.Hint },
      { id: Information.File.ID, hint: Information.File.Hint },
      { id: Information.Files.ID, hint: Information.Files.Hint },
      { id: Information.Table.ID, hint: Information.Table.Hint },
      { id: Information.List.ID, hint: Information.List.Hint },
      {
        id: 16,
        hint: "plain text such as words, sentences or paragraph with new lines",
      },
      { id: 17, hint: "beta supported (action)" },
      { id: 18, hint: "beta supported (hidden)" },
      { id: Information.Username.ID, hint: Information.Username.Hint },
      { id: Information.User.ID, hint: Information.User.Hint },
      { id: Information.Users.ID, hint: Information.Users.Hint },
      { id: 23, hint: "not supported (collaboration)" },
      { id: Information.Content.ID, hint: Information.Content.Hint },
      { id: Information.Context.ID, hint: Information.Context.Hint },
      { id: Information.Email.ID, hint: Information.Email.Hint },
      { id: Information.Phone.ID, hint: Information.Phone.Hint },
      { id: Information.Select.ID, hint: Information.Select.Hint },
      { id: Information.Tag.ID, hint: Information.Tag.Hint },
      { id: Information.Signature.ID, hint: Information.Signature.Hint },
      { id: Information.DateRange.ID, hint: Information.DateRange.Hint },
      { id: Information.Formula.ID, hint: Information.Formula.Hint },
      { id: Information.URL.ID, hint: Information.URL.Hint },
      // { id: 33, hint: "not supported (state)" },
    ],
  }),
  validations() {
    let formData = {};
    if (this.isSystem) {
      // system validation
    } else {
      let list = this.information;
      if (this.findParent) {
        list = Array.isArray(this.findParent.fields) ? this.findParent.fields : [];
      } else if (this.parentFields && Array.isArray(this.parentFields)) {
        list = this.parentFields;
      } else if (this.child) {
        list = [];
      }
      formData = validator.informationDesign(list);
      /* workaround */
      if (!this.isInternal) {
        formData.fieldName = {};
      }
      formData.displayName = {
        ...formData.displayName,
        nonReserved: (val) => validator.isInformationNonReserved(val, informationReservedKeywords)
      };
      /* end workaround */
      if ((this.formData.type == 27 || this.formData.type == 28) && !this.formData.reference) {
        formData.optionList = {
          $each: {
            name: {
              required,
              unique: (val, item) => {
                if (val && Array.isArray(this.formData.optionList)) {
                  const res = this.formData.optionList.filter((x) => 
                    x.name && x.name.toString().trim() == val.toString().trim()
                  );
                  return !(res.length > 1 && res[0].id !== item.id);
                } else {
                  return true;
                }
              }
            },
            $trackBy: "id",
          }
        }
      }
    }
    return { formData };
  },
  methods: {
    /**
     * 
     * @param {Object} item
     * @param {Function} saveCallback
     * @param {Function} continueCallBack
     * @param {Object} params 
     */
    async show(item, saveCallBack, continueCallBack, params) {
      this.continueCallBack = continueCallBack;
      this.formData = _.cloneDeep(item);

      /* backfill computed for formula fields created before the flag existed, so the
         field definition stays consistent on the next save */
      if (this.formData && this.formData.formulaMode && !this.formData.computed) {
        this.$set(this.formData, "computed", true);
      }
      /**
       * params properties
       * @property {Array} modules
       * @property {String} activityId
       * @property {Array} information
       * @property {Array} informationTypes
       * @property {String} parentId
       */
      this.params = params;

      if (this.isExist) {
        const { t, tid } = this.$route.query;
        if (!t && !tid) {
          commons.addDesignQueryParams(this.$router, this.$route, [
            { name: "t", value: "information" },
            { name: "tid", value: this.formData.id }
          ]);
        }
      }

      this.loading = true;

      return new Promise(async (resolve) => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            if (this.isReadOnly) {
              this.reset();
            } else {
              const diff = changesets.diff(this.formData, this.originalData);
              if (diff && diff.length) {
                this.$modal.show("dialog", {
                  title: "Confirm discard changes",
                  text: "Discard all changes?",
                  buttons: [
                    { title: "Cancel" },
                    {
                      title: "Discard",
                      default: true,
                      handler: () => {
                        this.$modal.hide("dialog");
                        this.reset();
                      },
                    },
                  ],
                });
              } else {
                this.reset();
              }
            }
          },
        });
        /* system:document id */
        if (this.formData && this.formData.id == "documentId") {
          this.formData.documentIdFormat = this.vector.documentIdFormat;
          try {
            const res = await canvasDesignStore.dispatch("retrieveSerialNumber");
            this.serialNumber = res.data.serial;
          } catch(err) {
            console.error(err);
            this.serialNumberError = true;
          }
        }
        /* connection type */
        if (this.isConnectionType) {
          try {
            await this.loadModules();
            const selectedModule = this.moduleOptions.find(
              (x) => x.id == this.formData.connectionId
            );
            if (selectedModule) {
              if (selectedModule.name !== this.formData.connection)
                this.$set(this.formData, "connection", selectedModule.name);
              if (selectedModule.id !== this.formData.connectionId)
                this.$set(this.formData, "connectionId", selectedModule.id);
            }
          } catch (err) {
            console.error(err);
          }
        }
        /* table simple (default) */
        if (this.formData.type === 14 && !this.isExist) {
          this.$set(this.formData, "simple", true);
        }
        /* reference */
        if (this.formData.type === 27 && this.formData.reference) {
          if (this.formData.referenceModuleId) {
            await this.loadReferenceModuleTable();
          }

          if (this.formData.referenceModuleFieldId) {
            const field = this.referenceInformation.find(
              x => x.id === this.formData.referenceModuleFieldId
            );
            if (field) {
              this.formData.referenceModule = field.displayName;
              this.formData.referenceModuleField = field.fieldName;
            } else {
              console.error("InformationDesign:Reference", "Undefined Reference Module Field");
            }
          }

          if (this.formData.referenceModuleTableId) {
            const field = this.referenceModuleTables.find(
              x => x.id === this.formData.referenceModuleTableId
            );
            if (field) {
              this.formData.referenceModuleTable = field.displayName;
            } else {
              console.error("InformationDesign:Reference", "Undefined Reference Module Table");
            }
          }

          if (this.formData.referenceDisplayFieldId) {
            const field = this.referenceDisplayFields.find(
              x => x.id === this.formData.referenceDisplayFieldId
            );
            if (field) {
              this.formData.referenceDisplayField = field.displayName;
            } else {
              console.error("InformationDesign:Reference", "Undefined Reference Display Field");
            }
          }
        }
        /* information type */
        if (this.formData.type === undefined) {
          await this.getAutoInformationType();
        }

        this.loading = false;
        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          if (!this.isReadOnly && !this.isSystem) {
            try {
              if (item.displayName && item.type === undefined) {
                this.$refs.informationTypeInput.$refs.element.getElementsByTagName('input')[0].focus();
              } else {
                this.$refs.designerName.focus();
                this.designNameHasFocus = true;
                if (!this.isExist && Boolean(this.formData.displayName)) {
                  this.$refs.designerName.select();
                }
              }
            } catch(err) {
              console.warn("[DOMException]", err);
            }
          }
        });
      });
    },
    async done() {
      this.compactData();
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        // D11: this save drops whatever sub-fields/options the user removed inside the
        // modal — the one delete path with no handler to intercept. Diff, sweep, and put
        // the referrer checklist in front of the save when anything still points at them.
        if (!(await this.guardDroppedSubUnits())) return; // cancelled — stay in the modal
        this.resolve && this.resolve(this.formData);
        this.reset();
      } else {
        console.warn(
          "InformationDesign:InvalidForm",
          validator.translateValidations(this.$v.formData, true)
        );
      }
    },
    /**
     * Returns false only when the user cancels. Interrupts solely when a dropped
     * sub-field/option still has real referrers (blocking/permission rows) — clean drops
     * save silently, exactly like before. Confirmed cascades run through the single apply
     * path first (recorded, revertible), then the save proceeds.
     */
    async guardDroppedSubUnits() {
      try {
        if (!canvasDesignStore.getters.canvasOpsEnabled) return true;
        if (this.params && this.params.parentId) return true; // sub-field editing — parent saves cover it
        const canvas = canvasDesignStore.state.canvas;
        const oldField = canvas && Array.isArray(canvas.information)
          ? canvas.information.find(x => x && x.id == this.formData.id)
          : null;
        if (!oldField) return true; // create — nothing can be dropped

        const guard = canvasOps.composeFieldSaveGuard(canvas, oldField, this.formData);
        const needsReview = guard.rows.some(r => r.kind === "blocking" || r.kind === "auth");
        if (!needsReview) return true;

        const droppedCount = guard.dropped.subFields.length + guard.dropped.options.length;
        const res = await this.$refs.ftsCanvasGuardDialog.show({
          mode: "delete",
          unitLabel: droppedCount === 1 ? "removed item" : "removed items",
          subject: `${droppedCount} sub-field/option removal(s) on '${this.formData.displayName}'`,
          composed: { rows: guard.rows, target: null },
        });
        if (res === false) return false;           // cancel — back to the modal
        if (res.mode === "legacy") return true;    // save anyway, referrers stay orphaned

        const result = await canvasDesignStore.dispatch("applyCanvasOps", {
          ops: res.ops,
          authRows: res.authRows,
          source: "guard",
          label: `Save '${this.formData.displayName}' — cleanup for ${droppedCount} removed sub-item(s)`,
        });
        if (!result.ok) {
          const first = result.errors.concat(...result.ops.map(o => o.errors))[0];
          this.$toasted.global.error({
            message: first ? first.message : "The cleanup could not be applied — nothing was saved.",
          });
          return false;
        }
        return true;
      } catch (err) {
        // the guard must never block a save through its own failure
        console.error("modal save guard failed — saving without it", err);
        return true;
      }
    },
    doneContinue() {
      if (!this.continuable) return;

      if (
        this.formData.type === 14 &&
        (!this.formData.fields ||
          (this.formData.fields && this.formData.fields.length === 0)) &&
        this.$v.formData.displayName &&
        !this.$v.formData.displayName.$invalid
      ) {
        this.openInformationField();
        return;
      }

      this.compactData();
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        const formData = _.cloneDeep(this.formData);
        const continueCallBack = Object.assign(this.continueCallBack);
        /* reset */
        this.reset();
        /* execute continueCallBack */
        continueCallBack && continueCallBack(formData);
        this.$toasted.info(`Information: ${formData.displayName} is added.`);
      } else {
        console.warn(
          "InformationDesign:InvalidForm",
          validator.translateValidations(this.$v.formData, true)
        );
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    async remove() {
      const res = await this.$refs.ftsInformationDeleteConfirmation.show({
        canvas: _.cloneDeep(canvasDesignStore.state.canvas),
        information: this.formData,
        payload: this.params && this.params.activityId ? { excluded: [this.params.activityId]} : null 
      });
      if (res === true) {
        setTimeout(() => {
          this.resolve && this.resolve(true);
          this.reset();
        }, 300);
      }
    },
    reset() {
      if (this.$route.query) {
        const { t, tid } = this.$route.query;
        if (t === "information" && tid === this.formData.id) {
          commons.removeDesignQueryParams(this.$router, this.$route, ["t", "tid"]);
        }
      }

      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    async saveSerial() {
      try {
        await canvasDesignStore.dispatch("saveSerialNumber", this.serialNumberPayload);
        this.serialNumberExpand = false;
        this.serialNumber = this.serialNumberPayload.serialNumber;
        this.$toasted.success(
          "Serial number is successfully set to " +
            this.serialNumberPayload.serialNumber
        );
      } catch (ex) {
        this.$toasted.error(ex.response.data.message);
      }
    },
    getHint(informationType) {
      if (informationType != null) {
        let hint = this.hints.find((x) => x.id == informationType);
        if(hint) {
          return hint['hint'];
        }
      }
      return null;
    },
    informationTypeSelected() {
      if (this.formData.type === 14) {
        this.$set(this.formData, "simple", true);
      }
    },
    async getAutoInformationType() {
      if (this.formData && this.formData.displayName) {
        const setInformationType = (type) => {
          if (type !== null && type !== undefined && (this.formData.type === null || this.formData.type === undefined)) {
            this.$set(this.formData, "type", type);
            this.informationTypeSelected();
          }
        }

        try {
          const res = await canvasDesignStore.dispatch("ai", {
            type: "information",
            text: this.formData.displayName,
          });
          if (this.getExcludeTypes.some((x) => x == res.data)) {
            setInformationType(0);
          } else if (this.informationTypes.some(x => x.id == res.data)) {
            setInformationType(res.data);
          } else {
            throw 'unaccessible information type';
          }
        } catch(err) {
          console.warn('InformationDesign:Type', err);
          setInformationType(0);
        }
      }
    },
    debounceInput: _.debounce(async (vm) => {
      await vm.getAutoInformationType();
      vm.autofillLoading = false;
    }, 800),
    dispatchAutofill() {
      if (
        !this.isExist
        && (this.formData.type === null || this.formData.type === undefined)
      ) {
        if (!this.autofillLoading) this.autofillLoading = true;
        this.debounceInput(this);
      }
    },
    async validateFormula() {
      const formula = this.formData && this.formData.formula;
      /* nothing to validate — clear feedback */
      if (!this.formulaMode || !formula) {
        this.formulaValidating = false;
        this.formulaError = null;
        return;
      }

      this.formulaValidating = true;
      try {
        /* Column (within-table) context: when this field lives in a table/list (findParent), the
           formula is a per-row column formula. Send the table id as parentId so the backend
           validates in row context, and send the table's columns as the schema so column('X')
           sibling refs resolve. Otherwise it is a top-level computed value: send the canvas
           information so references to unsaved fields still resolve. */
        const columnContext = this.isColumnContext;
        const res = await canvasDesignStore.dispatch("testFormula", {
          formula,
          /* sibling-column schema for column('X') refs; a not-yet-committed table has no fields
             array yet (no siblings to resolve), so fall back to an empty schema -- rowNumber() and
             literals still validate in row context via parentId below */
          information: columnContext
            ? (Array.isArray(this.findParent.fields) ? this.findParent.fields : [])
            : this.information,
          parentId: columnContext ? this.findParent.id : null,
          /* table display name: lets the backend treat a drill into this same table
             (e.g. Entry's Table's Unit Price) as the per-row value, not a cross-row list */
          parentName: columnContext ? this.findParent.displayName : null,
          /* the full working (unsaved) module schema, so references to fields/tables/columns that
             only exist in the current canvas resolve live — no save required first. Includes the
             in-design parent table (merged by formulaModuleInformation) so a column formula that
             drills Entry -> <this table> -> <sibling column> validates before the table is committed. */
          moduleInformation: this.formulaModuleInformation
        });
        const result = res && res.data;
        if (result && result.valid) {
          this.formulaError = null;
          /* the backend decides the information type for a computed field */
          if (result.type !== null && result.type !== undefined) {
            this.$set(this.formData, "type", result.type);
            this.informationTypeSelected();
          }
        } else {
          this.formulaError = (result && result.error) || "Invalid formula.";
        }
      } catch (err) {
        console.warn("InformationDesign:Formula", err);
        this.formulaError = "Unable to validate formula.";
      } finally {
        this.formulaValidating = false;
      }
    },
    debounceFormula: _.debounce((vm) => {
      vm.validateFormula();
    }, 600),
    error(key) {
      // workaround
      if (
        key === "displayName" &&
        this.$v.formData.displayName &&
        this.$v.formData.displayName.$error &&
        !this.$v.formData.displayName.nonReserved
      ) {
        const reducer = (res, val, ind, arr) =>
          ind
            ? ind === arr.length - 1
              ? `${res}, and ${val}`
              : `${res}, ${val}`
            : val;
        return `Oops! you cannot use reserved words: ${(Array.isArray(
          informationReservedKeywords
        )
          ? informationReservedKeywords
          : []
        ).reduce(reducer, null)}`;
      }
      // end workaround
      return validator.informationDesignError(this.$v.formData, key);
    },
    fieldValidator(parentFieldName, fieldId, fieldName) {
      try {
        return validator.fieldValidator(
          this.$v.formData[parentFieldName].$each.$iter,
          fieldId,
          fieldName
        );
      } catch(err) {
        console.error("InformationDesign:FieldValidator", err);
      }
    },
    fieldValidatorTouch(parentFieldName, fieldId, fieldName) {
      try {
        validator.fieldValidatorTouch(
          this.$v.formData[parentFieldName].$each.$iter,
          fieldId,
          fieldName
        );
      } catch(err) {
        console.error("InformationDesign:FieldValidatorTouch", err);
      }
    },
    fieldValidatorInvalid(parentFieldName, fieldId, fieldName) {
      try {
        return validator.fieldValidatorInvalid(
          this.$v.formData[parentFieldName].$each.$iter,
          fieldId,
          fieldName
        );
      } catch(err) {
        console.error("InformationDesign:fieldValidatorInvalid", err);
      }
    },
    informationIcon(id) {
      return commons.informationIcon(id) || "fts-icon-text";
    },
    getModule(id) {
      if (id && this.workspaceModules) {
        const module = this.workspaceModules.find(x => x.id == id);
        if (module) {
          return {
            id: module.id,
            name: module.name,
            emoji: module.emoji,
          }
        }
      }
    },
    compactData() {
      if (this.formData.type != 0) {
        this.$delete(this.formData, "uniqueKey");
      }

      if (!this.isConnectionType) {
        this.updateConnection();
      }

      if (this.formData.type != 27 && this.formData.type != 28) {
        this.$delete(this.formData, "optionList");
        this.$delete(this.formData, "reference");
        this.updateReferenceModule();
      } else {
        if (this.formData.reference === true) {
          this.$delete(this.formData, "optionList");
        } else {
          this.$delete(this.formData, "reference");
          this.updateReferenceModule();
        }
      }
    },
    /* display template */
    updateDisplayTemplate(val) {
      if (!val && this.formData.valueTemplate)
        delete this.formData.valueTemplate;
    },
    /* fields */
    async openInformationField(selected) {
      if (commons.elementStopPropagation()) return;

      let vm = this;
      let item = { ...selected };

      if (!item.id) {
        item = {
          id: commons.generateFieldName(),
          fieldName: commons.generateFieldName(),
        };
      }

      let items = this.formData.fields;

      function set(result, save) {
        if (!items) {
          vm.$set(vm.formData, "fields", []);
          items = vm.formData.fields;
        }
        const findIndex = items.findIndex((x) => x.id == item.id);
        if (findIndex > -1) {
          vm.$set(items, findIndex, result);
        } else {
          items.push(result);
        }
      }
      const result = await this.$refs.ftsInformationDesignModal.show(
        item,
        (formData) => {
          set(formData, true);
        },
        (continueFormData) => {
          set(continueFormData);
          this.openInformationField();
        },
        { disabledDelete: this.params && this.params.disabledDelete}
      ); 
      if (result != null && result != true) {
        set(result);
      } else if (result == true) {
        const findIndex = items.findIndex((x) => x.id == item.id);
        if (findIndex > -1) items.splice(findIndex, 1);
      }
    },
    async removeInformationField(item) {
      const res = await this.$refs.ftsInformationDeleteConfirmation.show({
        canvas: _.cloneDeep(canvasDesignStore.state.canvas),
        information: item,
        payload: this.params && this.params.activityId ? { excluded: [this.params.activityId]} : null 
      }); 

      if (res === true) {
        this.formData.fields.splice(this.formData.fields.indexOf(item), 1);
      }
    },
    /* select */
    toggleSelectSwitch(val) {
      if (Boolean(val)) {
        this.$set(this.formData, "reference", true);
      } else {
        this.$delete(this.formData, "reference");
        this.updateReferenceModule();
      }
    },
    addSelectOption() {
      this.$v.formData.optionList.$touch();
      if (!this.$v.formData.optionList.$error) {
        !this.formData.optionList && this.$set(this.formData, "optionList", []);
        const option = {
          id: commons.newUUID(),
          name: null,
        };
        this.formData.optionList.push(option);
        this.$nextTick(() => {
          this.$refs[
            `option${this.formData.optionList.indexOf(option)}`
          ][0].$el.focus();
        });
      }
    },
    removeSelectOption(item) {
      this.formData.optionList.splice(
        this.formData.optionList.indexOf(item),
        1
      );
    },
    getOptionColor(item) {
      return commons.selectOptionBackground({
        option: item,
        optionList: this.formData.optionList,
      });
    },
    /* connection */
    async loadModules() {
      try {
        const res = await canvasDesignStore.dispatch("loadVectorSelection");
        const moduleOptions = res.data ? res.data : [];
        if (this.formData.type === 20 || this.formData.type === 21)
          this.moduleOptions = moduleOptions.filter((x) => x.isUserCollection);
        else 
          this.moduleOptions = moduleOptions;
      } catch(err) {
        this.moduleOptions = [];
        console.error("InformationDesign:LoadModules", err);
      }
    },
    async preloadModules(loading) {
      loading(true);
      await this.loadModules();
      loading(false);
    },
    updateConnection(item) {
      // connection name
      if (item && item.name) 
        this.$set(this.formData, "connection", item.name);
      else if (this.formData.connection)
        this.$delete(this.formData, "connection");
      // connection id
      if (!item && this.formData.connectionId)
        this.$delete(this.formData, "connectionId");
      // reset connection field
      this.updateConnectionField();
    },
    updateConnectionField(item) {
      // connection field Id
      if (item && item.id)
        this.$set(this.formData, "connectionFieldId", item.id);
      else 
        this.$delete(this.formData, "connectionFieldId");
      // connection field name
      if (item && item.fieldName)
        this.$set(this.formData, "connectionFieldName", item.fieldName);
      else if (this.formData.connectionFieldName)
        this.$delete(this.formData, "connectionFieldName");
      // connection field
      if (!item && this.formData.connectionField)
        this.$delete(this.formData, "connectionField");
    },
    /* reference */
    async loadReferenceModuleTable() {
      try {
        const res = await canvasDesignStore.dispatch("loadModuleInformation", this.formData.referenceModuleId);
        this.referenceModuleTables = res.filter(x => x.type == 14);
      } catch(err) {
        this.referenceModuleTables = [];
        console.error("InformationDesign:PreloadReferenceModuleTable", err);
      }
    },
    async preloadReferenceModuleTable(loading) {
      loading(true); 
      await this.loadReferenceModuleTable();
      loading(false);
    },
    async updateReferenceModule(item) {
      /* reset reference module table */
      this.updateReferenceModuleTable();
      /* update */
      if (item && item.displayName) {
        this.$set(this.formData, "referenceModule", item.displayName);
        this.$set(this.formData, "referenceModuleId", item.connectionId);
        this.$set(this.formData, "referenceModuleField", item.fieldName);
        await this.loadReferenceModuleTable();
        if (this.referenceModuleTables.length) {
          this.$set(this.formData, "referenceModuleTable", this.referenceModuleTables[0].displayName);
          this.updateReferenceModuleTable(this.referenceModuleTables[0]);
        }
      } else {
        this.$delete(this.formData, "referenceModule");
        this.$delete(this.formData, "referenceModuleId");
        this.$delete(this.formData, "referenceModuleField");
        this.$delete(this.formData, "referenceModuleFieldId");
      }
    },
    updateReferenceModuleTable(item) {
      /* reset reference display field */
      this.updateReferenceDisplayField();
      /* update */
      if (item && item.id) {
        this.$set(this.formData, "referenceModuleTableId", item.id);
        if (this.referenceDisplayFields.length) {
          this.$set(this.formData, "referenceDisplayField", this.referenceDisplayFields[0].displayName);
          this.updateReferenceDisplayField(this.referenceDisplayFields[0]);
        }
      } else {
        this.$delete(this.formData, "referenceModuleTable");
        this.$delete(this.formData, "referenceModuleTableId");
      }
    },
    updateReferenceDisplayField(item) {
      if (item && item.id) {
        this.$set(this.formData, "referenceDisplayFieldId", item.id);
      } else {
        this.$delete(this.formData, "referenceDisplayField");
        this.$delete(this.formData, "referenceDisplayFieldId");
      }
    },
    /* drag and drop */
    draggableOnDrag(reference, condition) {
      if (condition)
        this.$refs[reference] &&
          this.$refs[reference].$el &&
          this.$refs[reference].$el.classList.add("drag");
      else
        this.$refs[reference] &&
          this.$refs[reference].$el &&
          this.$refs[reference].$el.classList.remove("drag");
    },
  },
  computed: {
    vector() {
      return canvasDesignStore.state.vector;
    },
    isDevelopment() {
      return authorizationStore.getters.isDevelopment;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    /* a computed (formula) field has its information type decided by the backend,
       so the type selector is locked */
    isComputed() {
      return !!(this.formData && this.formData.computed);
    },
    isBeta() {
      return authorizationStore.getters.isBeta || canvasDesignStore.getters.workspaceBeta;
    },
    workspaceModules() {
      return this.params && this.params.modules ? this.params.modules : canvasDesignStore.getters.workspaceModules;
    },
    information() {
      return this.params && this.params.information ? this.params.information : canvasDesignStore.getters.canvasInformation;
    },
    referenceInformation() {
      const information = this.information.filter(x => x.type == 7 || x.type == 20);
      if (this.findParent && Array.isArray(this.findParent.fields)) {
        const fields = this.findParent.fields.filter(
          x => x.type == 7 || x.type == 20
        ).map(
          y => ({ 
            ...y,
            parentDisplayName: this.findParent.displayName,
            parentType: this.findParent.type,
          })
        );
        return information.concat(fields);
      }
      return information;
    },
    referenceDisplayFields() {
      try {
        const field = this.referenceModuleTables.find(
          x => x.id === (this.formData && this.formData.referenceModuleTableId)
        );
        if (field && field.fields) return field.fields;
      } catch(err) {
        console.error("InformationDesign:ReferenceDisplayFields", err);
      }
      return [];
    },
    findInformation() {
      return _.findDeep(
        this.information,
        (x) => x.id == this.formData.id,
        { childrenPath: "fields" }
      );
    },
    findParent() {
      if (this.parent) {
        return this.parent;
      } else if (this.params && this.params.parentId) {
        const parent =  _.findDeep(
          this.information,
          (x) => x.id == this.params.parentId,
          { childrenPath: "fields" }
        );
        if (
          parent &&
          parent.value &&
          (parent.value.type == 14 || parent.value.type == 15)
        ) return parent && parent.value;
      } else if (
        this.findInformation &&
        this.findInformation.parent &&
        this.findInformation.parent.id &&
        (this.findInformation.parent.type == 14 || this.findInformation.parent.type == 15)
      ) {
        return this.findInformation.parent;
      }
    },
    isExist() {
      return Boolean(this.findInformation);
    },
    informationTypes() {
      if (this.params && this.params.informationTypes) {
        return this.params.informationTypes;
      }
      return commons.informationTypes.filter(x => 
        !(
          x.internal ||
          (this.isTableField && (x.id === 14 || x.id === 15)) ||
          (this.isListField && x.id === 15) ||
          (x.isAlpha && !this.isInternal) ||
          (x.isBeta && !this.isBeta)
        )
      );
    },
    getInformationTypeOptions() {
      if (
        !(this.params && this.params.informationTypes) &&
        this.formData.id !== null && 
        this.formData.id !== undefined &&
        !(this.informationTypes.some(x => x.id === this.formData.type ))
      ) {
        const informationType = commons.informationTypes.find(x => x.id === this.formData.type);
        if (informationType)
          return [...this.informationTypes, informationType];
      }
      return this.informationTypes;
    },
    isSystem() {
      return this.formData && this.formData.system;
    },
    isConnectionType() {
      return (
        this.formData && [7, 8, 20, 21, 24].indexOf(this.formData.type) > -1
      );
    },
    isTableField() {
      return Boolean(this.findParent && this.findParent.type == 14);
    },
    isListField() {
      return Boolean(this.findParent && this.findParent.type == 15);
    },
    /* Within-table column context: true whenever this field lives inside a table/list (type 14/15),
       which is what makes the per-row rowNumber()/Row Auto Number meaningful. Keyed off the parent
       TYPE, not findParent.fields being populated -- a freshly-added column on a not-yet-committed
       table has no fields array yet, but it is still a column. */
    isColumnContext() {
      return this.isTableField || this.isListField;
    },
    continuable() {
      return !this.isSystem && !this.isExist && !this.disableContinue && Boolean(this.continueCallBack);
    },
    displayTemplateOptions() {
      let options = [];

      if (this.formData && this.formData.connectionId) {
        const module = this.moduleOptions.find(x => x.id == this.formData.connectionId)

        if (module && Array.isArray(module.information)) {
          const allowedTypes = [0, 2, 3, 4, 7, 20, 25, 26, 27];
          return module.information.filter(x => allowedTypes.indexOf(x.type) > -1);
        }
      }

      return options;
    },
    displayInformationSelection() {
      if (this.formData && this.formData.connectionId) {
        const module = this.moduleOptions.find((x) =>
          x.id == this.formData.connectionId
        );
        const allowedTypes = [0, 2, 3, 4, 7, 20, 25, 26, 27];
        return Array.isArray(module && module.information) ?
          module.information.reduce((accumulator, currentValue) => {
            if (allowedTypes.indexOf(currentValue.type) > -1 || (this.isInternal && currentValue.type == 16)) {
              return [...accumulator, {...currentValue}];
            } else if (!Object.hasOwn(currentValue, 'type') && currentValue.fieldName === '$DocumentId') {
              if (this.isDevelopment) {
                return [
                  ...accumulator,
                  {displayName: 'Display Name (dev)', fieldName: '$DisplayName', system: true},
                  {...currentValue, system: true}
                ];
              }
              return [...accumulator, {...currentValue, system: true}];
            }
            return accumulator;
          }, []) : [];
      }
    },
    headerHintHelper() {
      return (
        this.continuable &&
        this.designNameHasFocus &&
        !this.autofillLoading &&
        !this.isExist &&
        this.formData &&
        this.formData.displayName &&
        this.$v.formData &&
        !this.$v.formData.$error &&
        this.$v.formData.type &&
        !this.$v.formData.type.$invalid
      );
    },
    advancedSettingsError() {
      const fields = ["fieldName"];
      return fields.some(x => {
        try {
          return this.$v.formData[x] && this.$v.formData[x].$error;
        } catch(err) {
          console.error("InformationDesign:AdvancedSettingsError", err);
        }
      });
    },
    isReadOnly() {
      const systemDefaults = ['documentId', 'title'];
      return (
        (!this.isSystem && (this.isExist && !this.isCapableChangeInformation) || (!this.isExist && !this.isCapableAddInformation)) ||
        (this.isSystem && systemDefaults.indexOf(this.formData && this.formData.id) === -1)
      );
    },
    formulaMode: {
      get() {
        return this.formData ? this.formData.formulaMode : false;
      },
      set(val) {
        if (!this.formData) return;
        this.$set(this.formData, "formulaMode", val);
        if (val) {
          /* a formula field is a computed field — its value is derived, not entered */
          this.$set(this.formData, "computed", true);
        } else {
          this.$delete(this.formData, "formula");
          this.$delete(this.formData, "computed");
        }
      }
    },
    formulaVariables() {
      // Everything goes through the single Entry root -- including a computed column, which drills
      // Entry -> <Table> -> <Column>. We deliberately do NOT surface standalone column shortcuts (the
      // table's columns are reachable by drilling into Entry); funnelling all field access through
      // Entry guarantees a client-computable native reference: an outer field becomes
      // getVariable("Entry", "<field>"), and a same-table column is rewritten by the native converter
      // to the per-row colField("<Column>"). (A standalone column('X') token is classified Complex /
      // non-native and would be rejected at save by the computed-field client-computability gate.)
      return [
        {
          name: "Entry",
          fieldName: "entry",
          type: 35,
          moduleId: canvasDesignStore.state.vectorId && canvasDesignStore.state.vectorId.toString()
        }
      ];
    },
    formulaModuleInformation() {
      /* The live working module information, with the in-design parent table merged in (by id) so a
         column formula can drill Entry -> <this table> -> <sibling column> before the table is
         committed to the canvas (a brand-new table only lands in state.canvas.information on
         OK/Continue, so it would otherwise be absent from the Entry autocomplete). */
      const base = Array.isArray(this.information) ? this.information.slice() : [];
      if (this.isColumnContext && this.findParent && this.findParent.id) {
        const idx = base.findIndex(x => x.id == this.findParent.id);
        if (idx > -1) base.splice(idx, 1, this.findParent);
        else base.push(this.findParent);
      }
      return base;
    },
    getExcludeTypes() {
      return Array.isArray(this.params && this.params.excludeTypes) ? this.params.excludeTypes : [];
    }
  },
  watch: {
    "formData.type": function(val, oldVal) {
      if (
        val != null && val != undefined &&
        oldVal != null && oldVal !== undefined
      ) {
        /* reset connection */
        if (this.isConnectionType) this.updateConnection();
      }
    },
    "formData.formula": function() {
      /* debounce a backend check that validates the formula and decides the type */
      if (this.formulaMode) {
        this.formulaValidating = true;
        this.debounceFormula(this);
      }
    },
    formulaMode: function(val) {
      /* clear any stale validation state when formula mode is turned off */
      if (!val) {
        this.formulaValidating = false;
        this.formulaError = null;
      }
    }
  },
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-information-design-modal {
  /* formula validation feedback */
  .design-modal-form-hint {
    margin-top: .125rem;
    margin-bottom: .375rem;
    font-size: .75rem;
    line-height: 1rem;
    color: var(--fts-form-helper);
  }
  /* parent */
  .information-parent {
    max-width: 140px;
    display: flex;
    align-items: center;
    margin-left: 0.75rem;
    font-size: 0.875rem;
    .information-parent-seperator {
      margin-right: 0.5rem;
      color: var(--fts-default);
    }
  }
  /* hint-helper */
  .information-type-hint-helper {
    margin-top: 0.375rem;
    font-size: $font-size;
    line-height: $line-height;
    color: var(--fts-form-helper);
  }
  /* options */
  .information-options {
    &.drag {
      .option-action {
        visibility: hidden;
      }
    }
    .option-wrapper {
      position: relative;
      min-width: 0;
      display: flex;
      align-items: center;
      margin-left: -0.75rem;
      padding: 0.275rem 0 0.275rem 0.75rem;
      &:hover {
        .option-handle {
          display: block;
        }
      }
      &.sortable-drag {
        .option-action {
          visibility: hidden;
        }
      }
      .option-handle {
        @include absolute-center-y;
        left: 0;
        display: none;
        font-size: 0.75rem;
        color: var(--fts-default);
        cursor: grab;
      }
      .option-l {
        flex: 1;
        display: flex;
        align-items: center;
        padding: 0.125rem 0;
        .option-name {
          width: auto;
          font-size: 1rem;
          padding: 0.375rem 0.575rem;
          border-radius: 0.375rem;
        }
      }
      .option-action {
        display: flex;
        align-items: center;
        margin-left: 0.75rem;
      }
      .option-color {
        margin-right: 0.75rem;
        @include flex-center-middle;
        &.vue-swatches {
          .vue-swatches__trigger {
            height: 20px !important;
            width: 20px !important;
            min-width: 20px;
          }
          .vue-swatches__container {
            position: absolute;
            right: 0px;
          }
        }
      }
    }
    .option-error {
      margin-top: -0.175rem;
      margin-bottom: .175rem;
      font-size: .75rem;
      color: var(--fts-danger);
    }
    .option-btn-add {
      margin-top: 0.375rem;
    }
  }
  /* fields */
  .information-fields {
    &.drag {
      .field-action {
        visibility: hidden;
      }
    }
    .field-wrapper {
      position: relative;
      min-width: 0;
      display: flex;
      align-items: center;
      margin-left: -0.75rem;
      padding: 0.275rem 0 0.275rem 0.75rem;
      &:hover {
        cursor: pointer;
        .field-handle {
          display: block;
        }
      }
      &.sortable-drag {
        .field-action {
          visibility: hidden;
        }
      }
      .field-handle {
        @include absolute-center-y;
        left: 0;
        display: none;
        font-size: 0.75rem;
        color: var(--fts-default);
        cursor: grab;
      }
      .information-type {
        padding: 0.5rem 0;
        flex: 1;
      }
      .field-action {
        margin-left: 0.75rem;
      }
    }
    .field-btn-add {
      margin: 0.375rem 0;
    }
  }
  /* serial number */
  .serial-number {
    .design-modal-label-group .design-modal-label-group-r {
      width: 75px;
      display: flex;
      justify-content: end;
    }
    .serial-number-action {
      margin-top: -1rem;
    }
  }
  /* select */
  .information-select-switch {
    @include flex-gap($gap: 0.375rem, $display: flex);
    margin-bottom: 0.75rem;
    .select-switch {
      &.select-switch--active {
        @include theme {
          background: rgba(theme-get('fts-primary'), 0.095);
        }
        color: var(--fts-primary);
      }
      @include theme {
        background: rgba(theme-get('fts-form-label'), 0.095);
      }
      display: inline-flex;
      align-items: flex-start;
      padding: 0.575rem 1rem;
      border-radius: 0.25rem;
      color: var(--fts-form-label);
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
    }
  }
}
/* information tag */
@mixin information-tag($flexShrink: null, $maxWidth: null) {
  @if $flexShrink {
    flex-shrink: $flexShrink;
  }
  @if $maxWidth {
    max-width: $maxWidth;
  }
  padding: 0.15rem 0.2rem;
  border-radius: 0.25rem;
  background-color: var(--fts-gray-100);
  font-size: 0.75rem;
  color: var(--fts-default);
  line-height: 1;
  &:not(:first-child) {
    margin-left: 0.375rem;
  }
}
/* information type */
.information-type {
  min-width: 0;
  display: flex;
  align-items: center;
  font-size: 1rem;
  .information-type-icon {
    margin-left: 0.175rem;
    margin-right: 0.375rem;
  }
  .information-type-name {
    @include text-overflow-ellipsis;
  }
  .information-hint {
    @include text-overflow-ellipsis;
    color: var(--fts-default);
    font-size: 0.75rem;
  }
  /* information */
  .information-module-tag {
    @include information-tag($flexShrink: 0, $maxWidth: 110px);
  }
  /* information parent tag */
  .information-parent-tag {
    @include information-tag($flexShrink: 0, $maxWidth: 110px);
    min-width: 0;
    display: flex;
    align-items: center;
    .information-parent-tag-seperator {
      margin-right: 0.375rem;
    }
  }
}
.fts-information-design-modal-type__dropdown-menu {
  .vs__dropdown-option {
    position: relative;
    &:hover {
      .information-hint-unsupported {
        display: block;
      }
    }
    .information-type-name {
      overflow: visible;
    }
    .information-hint-unsupported {
      @include absolute-center-y;
      right: 0.5rem;
      display: none;
      padding: 0.125rem 0.25rem;
      border: 1px solid var(--fts-border);
      border-radius: 0.25rem;
      background-color: var(--fts-popover-alt-background);
      font-size: 0.75rem;
      z-index: 1;
    }
  }
}
/* module option emoji */
.design-modal-module-option-emoji {
  margin-right: 0.575rem !important;
}
</style>